<template>
  <!-- A scene drawn from a config in data/feature-motion.js: a vertical stack of blocks, each a
       slice of a real app screen. Times (`at`, `swap`, `press.at`) are fractions of the cycle;
       every block's resting style is the final frame (see the kit in FeatureMotion.vue). A value
       with a `…To` twin flips to it at `swap`. -->
  <div class="ms">
    <template v-for="(block, index) in blocks" :key="index">
      <!-- A record header: icon or initials, title, subtitle and a status pill (MotionPill). -->
      <div v-if="block.type === 'head'" class="ms-head fm-rise" :style="beat(block.at)">
        <span v-if="block.avatar" class="ms-avatar" translate="no">{{ block.avatar }}</span>
        <span v-else-if="block.icon" class="ms-tile"><svg viewBox="0 0 24 24" aria-hidden="true"><path :d="motionIcons[block.icon]" /></svg></span>
        <span class="ms-copy">
          <strong>{{ block.title }}</strong>
          <small v-if="block.sub">{{ block.sub }}</small>
        </span>
        <MotionPill v-if="block.pill" :pill="block.pill" :then="block.then" />
      </div>

      <!-- Label and value pairs (Repeats, Next invoice, Total Paid). A field with a `pill` shows a
           status chip instead; with `press`, the pointer taps the chip then and a ring pulses on it,
           as someone picks the next status from the chip's menu. -->
      <dl v-else-if="block.type === 'fields'" class="ms-fields fm-rise" :style="beat(block.at)">
        <div v-for="field in block.items" :key="field.label">
          <dt>{{ field.label }}</dt>
          <dd v-if="field.pill" class="ms-pick" :class="{ 'fm-focus': field.press }" :style="field.press ? { '--at': field.press, '--grow': '3px', '--radius': '999px' } : null">
            <MotionPill :pill="field.pill" :then="field.then" />
            <template v-if="field.press">
              <span class="fm-ripple" :style="{ '--press': field.press }"></span>
              <span class="fm-pointer" :style="{ '--press': field.press }">
                <svg viewBox="0 0 12 18" aria-hidden="true"><path d="M0 0 0 15 3.8 11.6 6.6 18 9.2 16.9 6.5 10.6 11.5 10.6Z" /></svg>
              </span>
            </template>
          </dd>
          <dd v-else class="fm-stack">
            <span :class="{ 'fm-out': field.to }" :style="field.to ? beat(field.swap) : null">{{ field.value }}</span>
            <span v-if="field.to" class="fm-rise" :style="beat(field.swap)">{{ field.to }}</span>
          </dd>
        </div>
      </dl>

      <!-- A list that fills in row by row (`step` apart), under an optional section label. The
           label can carry a link the pointer presses (`labelAction`), gone again at `out`. -->
      <div v-else-if="block.type === 'rows'" class="ms-rows">
        <p v-if="block.label" class="ms-label fm-rise" :style="beat(block.at)">
          <span>{{ block.label }}</span>
          <span v-if="block.labelAction" class="ms-label-link">
            <span class="fm-out" :style="beat(block.labelAction.out)">{{ block.labelAction.text }}</span>
            <span class="fm-ripple" :style="{ '--press': block.labelAction.press }"></span>
            <span class="fm-pointer" :style="{ '--press': block.labelAction.press }">
              <svg viewBox="0 0 12 18" aria-hidden="true"><path d="M0 0 0 15 3.8 11.6 6.6 18 9.2 16.9 6.5 10.6 11.5 10.6Z" /></svg>
            </span>
          </span>
        </p>
        <div
          v-for="(row, rowIndex) in block.items"
          :key="row.title"
          class="ms-row fm-rise"
          :class="{ 'fm-focus': row.focus, 'ms-row-wrap': row.wrap }"
          :style="{ ...beat(row.at ?? block.at + (rowIndex + (block.label ? 1 : 0)) * (block.step ?? 0.04)), '--grow': '0px', '--radius': '6px' }"
        >
          <!-- A selection checkbox, ticked at `check`. -->
          <span v-if="row.check" class="ms-check fm-stack">
            <span class="ms-check-off"></span>
            <span class="ms-check-on fm-rise" :style="{ ...beat(row.check), '--len': 0.04 }"><svg viewBox="0 0 24 24" aria-hidden="true"><path :d="motionIcons.check" /></svg></span>
          </span>
          <span v-if="row.logo" class="ms-logo"><img :src="row.logo" alt="" width="16" height="16" /></span>
          <span v-else-if="row.avatar" class="ms-avatar ms-avatar-sm" translate="no">{{ row.avatar }}</span>
          <span v-else-if="row.icon" class="ms-row-icon" :class="row.iconTone && `ms-ink-${row.iconTone}`"><svg viewBox="0 0 24 24" aria-hidden="true"><path :d="motionIcons[row.icon]" /></svg></span>
          <span class="ms-copy">
            <strong :class="{ 'ms-mono': row.mono }" :translate="row.mono ? 'no' : null">{{ row.title }}</strong>
            <small v-if="row.sub" class="fm-stack">
              <span :class="{ 'fm-out': row.subTo }" :style="row.subTo ? beat(row.swap) : null">{{ row.sub }}</span>
              <span v-if="row.subTo" class="fm-rise" :style="beat(row.swap)">{{ row.subTo }}</span>
            </small>
          </span>
          <span v-if="row.value || row.pill || row.pills" class="ms-row-end">
            <span v-if="row.value" class="ms-value" :class="row.valueTone && `ms-ink-${row.valueTone}`">{{ row.value }}</span>
            <MotionPill v-if="row.pill" :pill="row.pill" :then="row.then" />
            <!-- Badges that add up (an inbox row lists every channel the conversation has had). -->
            <span v-if="row.pills" class="ms-pills">
              <span v-for="badge in row.pills" :key="badge.text" :class="{ 'fm-rise': badge.at }" :style="badge.at ? beat(badge.at) : null">
                <MotionPill :pill="badge" />
              </span>
            </span>
          </span>
          <span v-if="row.button" class="ms-mini-btn">{{ row.button }}</span>
          <!-- A switch that turns on at `toggle.at` (off before, on at rest). -->
          <span v-if="row.toggle" class="ms-switch fm-stack">
            <span class="ms-switch-off fm-out" :style="beat(row.toggle.at)"><i /></span>
            <span class="ms-switch-on fm-rise" :style="{ ...beat(row.toggle.at), '--len': 0.06 }"><i /></span>
          </span>
        </div>
      </div>

      <!-- A stacked bar (the AR Aging bucket bar): segments sized by value, each rising in turn,
           with a chip per segment (dot, label, amount) underneath. -->
      <div v-else-if="block.type === 'bars'" class="ms-bars">
        <div class="ms-bar">
          <span
            v-for="(segment, segmentIndex) in block.segments"
            :key="segment.label"
            class="fm-rise"
            :class="`ms-fill-${segment.tone}`"
            :style="{ ...beat(block.at + segmentIndex * (block.step ?? 0.03)), flexGrow: segment.value }"
          ></span>
        </div>
        <div class="ms-chips fm-rise" :style="beat(block.at + block.segments.length * (block.step ?? 0.03))">
          <span v-for="segment in block.segments" :key="segment.label">
            <i :class="`ms-fill-${segment.tone}`" />{{ segment.label }} <b>{{ segment.amount }}</b>
          </span>
        </div>
      </div>

      <!-- A total line under line items. -->
      <div v-else-if="block.type === 'total'" class="ms-total fm-rise" :style="beat(block.at)">
        <span>{{ block.label }}</span>
        <strong>{{ block.value }}</strong>
      </div>

      <!-- Buttons the pointer presses (`press.index` at `press.at`). With `after`, the buttons go
           at `swap` and those blocks take their place, as the app swaps a confirmed action for its
           result. The pointer sits on its own layer so its ripple outlives the buttons. `lead` is a
           count before the buttons (a bulk bar's "2 selected"); `title` and `body` make it a
           confirm dialog with the buttons at its foot, laid over the block before it as the app's
           modal is, and closed again at `out`. -->
      <div v-else-if="block.type === 'action'" class="fm-stack ms-action" :class="{ 'ms-modal': block.title }">
        <div class="ms-control" :class="{ 'fm-out': block.after || block.out }" :style="block.after || block.out ? beat(block.out ?? block.swap) : null">
          <div :class="block.title ? 'ms-dialog fm-rise' : 'ms-control-inner'" :style="block.title ? { ...beat(block.at), '--len': 0.03 } : null">
            <strong v-if="block.title" class="ms-dialog-title">{{ block.title }}</strong>
            <p v-if="block.body" class="ms-dialog-body">{{ block.body }}</p>
            <div class="ms-buttons">
              <span v-if="block.lead" class="ms-btn ms-btn-lead fm-rise" :style="beat(block.at)">{{ block.lead }}</span>
              <span
                v-for="button in block.buttons"
                :key="button.label"
                class="ms-btn fm-rise"
                :class="button.primary ? 'ms-btn-primary' : 'ms-btn-secondary'"
                :style="beat(block.at)"
              >
                <svg v-if="button.icon" viewBox="0 0 24 24" aria-hidden="true"><path :d="motionIcons[button.icon]" /></svg>
                {{ button.label }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="block.press" class="ms-cursor" :class="{ 'ms-cursor-end': block.title }">
          <span class="fm-ripple" :style="pressAt(block)"></span>
          <span class="fm-pointer" :style="pressAt(block)">
            <svg viewBox="0 0 12 18" aria-hidden="true"><path d="M0 0 0 15 3.8 11.6 6.6 18 9.2 16.9 6.5 10.6 11.5 10.6Z" /></svg>
          </span>
        </div>
        <MotionScene v-if="block.after" class="ms-after" :blocks="block.after" />
      </div>

      <!-- The payoff: a tinted panel under the plan-tone ring, with an optional caption. -->
      <div v-else-if="block.type === 'payoff'" class="ms-payoff">
        <div class="ms-panel fm-rise fm-focus" :class="`ms-tone-${block.tone || 'green'}`" :style="{ ...beat(block.at), '--grow': '3px', '--radius': '12px' }">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="motionIcons[block.icon || 'checkCircle']" /></svg>
          <strong>{{ block.text }}</strong>
        </div>
        <p v-if="block.caption" class="ms-caption fm-rise" :style="beat(block.at + 0.08)">{{ block.caption }}</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { motionIcons } from "~/data/motion-icons.js";

defineProps({
  blocks: { type: Array, required: true },
});

const beat = (at) => ({ "--at": at });

// The pointer lands a little right of the pressed button's centre (buttons share the row).
const pressAt = (block) => ({
  "--press": block.press.at,
  left: `${((block.press.index + (block.lead ? 1 : 0) + 0.6) / (block.buttons.length + (block.lead ? 1 : 0))) * 100}%`,
});
</script>

<style>
.ms { display: grid; gap: 10px; }
.ms-head { display: flex; align-items: center; gap: 10px; }
.ms-avatar,
.ms-tile {
  display: grid;
  flex: none;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: var(--fm-sunken);
  box-shadow: inset 0 0 0 1px var(--fm-line);
  color: var(--fm-text);
  font-size: 11px;
  font-weight: 600;
}
.ms-logo {
  display: grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: var(--fm-sunken);
  box-shadow: inset 0 0 0 1px var(--fm-line);
}
.ms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.94em; }
.ms-switch { flex: none; }
.ms-switch > span { display: block; width: 32px; height: 18px; border-radius: 999px; padding: 2px; }
.ms-switch i { display: block; width: 14px; height: 14px; border-radius: 999px; background: #fff; box-shadow: 0 1px 2px rgb(0 0 0 / 0.25); }
.ms-switch-off { background: var(--fm-line); }
.ms-switch-on { background: var(--fm-green); }
.ms-switch-on i { margin-left: 14px; }
.ms-avatar-sm { width: 26px; height: 26px; font-size: 10px; }
.ms-tile { border-radius: 8px; }
.ms-tile svg { width: 16px; height: 16px; fill: var(--fm-muted); }
.ms-copy { display: grid; flex: 1; min-width: 0; }
.ms-copy strong,
.ms-copy small > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ms-copy strong { color: var(--fm-text); font-weight: 500; }
.ms-head .ms-copy strong { font-size: 13.5px; font-weight: 600; }
/* Phones: a header title wraps rather than truncate beside a wide status pill. */
@media (max-width: 639px) {
  .ms-head .ms-copy strong { white-space: normal; }
}
/* `wrap`: the app's own sentence is long, so it wraps rather than being cut. */
.ms-row-wrap .ms-copy strong,
.ms-row-wrap .ms-copy small > span { white-space: normal; }
.ms-copy small { min-width: 0; color: var(--fm-muted); font-size: 11px; }

.ms-fields { display: grid; margin: 0; border: 1px solid var(--fm-line); border-radius: 8px; }
.ms-fields > div { display: flex; justify-content: space-between; gap: 12px; padding: 6px 10px; }
.ms-fields > div + div { border-top: 1px solid var(--fm-line); }
.ms-fields dt { flex: none; color: var(--fm-muted); }
.ms-fields dd { min-width: 0; margin: 0; justify-items: end; color: var(--fm-text); font-weight: 500; font-variant-numeric: tabular-nums; }
.ms-fields dd > span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ms-fields > div:has(.ms-pick) { align-items: center; }
.ms-pick { position: relative; display: flex; }
.ms-pick .fm-pointer, .ms-pick .fm-ripple { top: 50%; left: 55%; }

.ms-rows { display: grid; border: 1px solid var(--fm-line); border-radius: 8px; }
.ms-label { display: flex; justify-content: space-between; gap: 10px; margin: 0; border-bottom: 1px solid var(--fm-line); padding: 6px 10px; color: var(--fm-muted); font-size: 11px; font-weight: 500; }
.ms-label-link { position: relative; color: var(--fm-blue); }
.ms-label-link .fm-pointer, .ms-label-link .fm-ripple { top: 55%; left: 60%; }
.ms-check { flex: none; width: 14px; height: 14px; }
.ms-check-off { border: 1.5px solid var(--fm-muted); border-radius: 4px; }
.ms-check-on { display: grid; place-items: center; border-radius: 4px; background: var(--fm-ink); }
.ms-check-on svg { width: 12px; height: 12px; fill: var(--fm-ink-text); }
.ms-row { display: flex; align-items: center; gap: 10px; min-height: 40px; padding: 5px 10px; }
.ms-row + .ms-row { border-top: 1px solid var(--fm-line); }
.ms-row-icon svg { display: block; width: 16px; height: 16px; fill: var(--fm-muted); }
.ms-row-end { display: flex; flex: none; align-items: center; gap: 10px; }
/* Phones: an amount and a pill stack, so the row's title keeps its width. */
@media (max-width: 639px) {
  .ms-row-end { display: grid; justify-items: end; gap: 3px; }
  /* Several badges wrap under each other rather than squeeze the name. */
  .ms-row-end:has(.ms-pills) { max-width: 50%; }
  .ms-pills { flex-wrap: wrap; justify-content: flex-end; }
}
.ms-pills { display: flex; gap: 4px; }
.ms-value { color: var(--fm-text); font-weight: 500; font-variant-numeric: tabular-nums; white-space: nowrap; }
.ms-ink-green, .ms-row-icon.ms-ink-green svg { color: var(--fm-green); fill: var(--fm-green); }
.ms-row-icon.ms-ink-blue svg { fill: var(--fm-blue); }
.ms-row-icon.ms-ink-red svg { fill: var(--fm-red); }
.ms-mini-btn {
  flex: none;
  border: 1px solid var(--fm-line);
  border-radius: 6px;
  padding: 3px 9px;
  color: var(--fm-text);
  font-size: 11px;
  font-weight: 500;
}

.ms-bars { display: grid; gap: 8px; }
.ms-bar { display: flex; gap: 2px; height: 8px; overflow: hidden; border-radius: 4px; }
.ms-bar > span { min-width: 4px; }
.ms-chips { display: flex; flex-wrap: wrap; gap: 4px 12px; color: var(--fm-muted); font-size: 11px; }
.ms-chips > span { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.ms-chips i { width: 6px; height: 6px; border-radius: 999px; }
.ms-chips b { color: var(--fm-text); font-weight: 500; font-variant-numeric: tabular-nums; }
.ms-fill-green { background: var(--fm-green); }
.ms-fill-amber { background: var(--fm-amber); }
.ms-fill-orange { background: var(--fm-orange); }
.ms-fill-red { background: var(--fm-red); }

.ms-total { display: flex; justify-content: space-between; padding: 0 10px; color: var(--fm-muted); }
.ms-total strong { color: var(--fm-text); font-size: 13px; font-weight: 650; font-variant-numeric: tabular-nums; }

.ms-action { margin-top: 2px; }
.ms-buttons { display: flex; gap: 8px; }
.ms-btn-lead { justify-content: flex-start; color: var(--fm-text); font-weight: 600; }
.ms-dialog { display: grid; gap: 6px; align-self: start; border: 1px solid var(--fm-line); border-radius: 10px; background: var(--fm-surface); padding: 12px; }
.ms-dialog-title { color: var(--fm-text); font-size: 13px; font-weight: 600; }
.ms-dialog-body { margin: 0 0 4px; color: var(--fm-muted); font-size: 11.5px; line-height: 1.45; }
.ms-btn {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 500;
  white-space: nowrap;
}
.ms-btn svg { width: 15px; height: 15px; fill: currentColor; }
.ms-btn-primary { background: var(--fm-ink); color: var(--fm-ink-text); }
.ms-btn-secondary { border: 1px solid var(--fm-line); color: var(--fm-text); }
.ms-cursor { position: relative; z-index: 2; height: 34px; align-self: start; pointer-events: none; }
.ms-cursor .fm-pointer, .ms-cursor .fm-ripple { top: 17px; }
.ms-cursor-end { align-self: end; margin: 0 12px 12px; }
.ms-action { position: relative; }
.ms-modal { position: absolute; z-index: 3; right: 0; bottom: 0; left: 0; }
.ms-modal .ms-dialog { box-shadow: 0 12px 32px rgb(0 0 0 / 0.18), 0 2px 6px rgb(0 0 0 / 0.08); }
.ms-after { align-self: start; }

.ms-panel { display: flex; align-items: center; gap: 10px; border-radius: 10px; padding: 10px 12px; color: var(--fm-text); }
.ms-panel svg { width: 18px; height: 18px; flex: none; }
.ms-panel strong { font-weight: 600; }
.ms-tone-green { background: var(--fm-green-fill); }
.ms-tone-green svg { fill: var(--fm-green); }
.ms-tone-blue { background: var(--fm-blue-fill); }
.ms-tone-blue svg { fill: var(--fm-blue); }
.ms-caption { margin: 8px 0 0; color: var(--fm-muted); font-size: 11px; }
</style>
